import { useRef, useState, useEffect, useMemo } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { INTAKE_COLORS, INTAKE_FONT } from "../../theme/intakeTokens";
import { useAuth } from "../../context/AuthContext";
import {
  formatAgentReply,
  formatScoreChangePill,
  getUserInitials,
  isGeoValidationReply,
  isSystemUserMessage,
  placeScorePills,
} from "../../utils/chatUtils";
import { validateIntakeText } from "../../utils/intakeTextValidation";
import {
  INTAKE_UPLOAD_ACCEPT,
  INTAKE_UPLOAD_TOO_LARGE_MSG,
  INTAKE_UPLOAD_UNSUPPORTED_MSG,
  intakeUploadSizeError,
  isIntakeUploadTooLarge,
  isIntakeUploadTypeAllowed,
} from "../../utils/intakeUploadLimits";
import { ReactComponent as UpArrowIcon } from "../../asserts/logo/UpArrow.svg";
import { ReactComponent as BlueFilledAlertIcon } from "../../asserts/logo/BlueFilledAlertIcon.svg";
import { ReactComponent as UploadButtonIcon } from "../../asserts/logo/UploadButton.svg";
import LogoIcon from "../../asserts/logo/Icon.svg";
import { ReactComponent as DocIcon } from "../../asserts/logo/DocIcon.svg";
import OutputCard from "./OutputCard";
import ChatMessageBubble, {
  isAssistantMessage,
  isUserTypedMessage,
} from "./ChatMessageBubble";
import EssentialsCompleteCard from "./EssentialsCompleteCard";
import ExtractionProgressCard from "./ExtractionProgressCard";
import "./intakeChatMessages.css";

const SEND_BTN_SHADOW = "0px 1px 2px 0px #0000000A";
const SEND_BTN_BG_DEFAULT =
  "linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";
const SEND_BTN_BG_HOVER =
  "linear-gradient(180deg, rgba(0, 0, 0, 0.08) 0%, rgba(0, 0, 0, 0.08) 100%), linear-gradient(0deg, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.25) 100%), linear-gradient(0deg, #005BF0, #005BF0)";

const DOC_MIN_CHARS = 500;
const DOC_CLIPBOARD_MIN_CHARS = 300;

/** True only for real documents — not short chat answers or workflow lines. */
function looksLikeDocument(text, fromClipboard = false) {
  const t = (text || "").trim();
  if (!t) return false;
  const lineCount = t.split(/\n/).filter((l) => l.trim()).length;
  if (fromClipboard && t.length >= DOC_CLIPBOARD_MIN_CHARS) return true;
  if (t.length >= DOC_MIN_CHARS) return true;
  if (t.length >= 300 && lineCount >= 3) return true;
  return false;
}

/** Matches the user avatar circle so both speakers align. */
const SYSTEM_MSG_LOGO_WIDTH = 17.42;
const SYSTEM_MSG_LOGO_HEIGHT = 20.47;
const CHAT_FONT = "Lato, sans-serif";

const SYSTEM_MSG_NAME_SX = {
  fontFamily: CHAT_FONT,
  fontWeight: 900,
  fontSize: 14,
  lineHeight: "19px",
  letterSpacing: 0,
  color: "#383838",
};

const SYSTEM_MSG_TURN_SX = {
  fontFamily: CHAT_FONT,
  fontWeight: 600,
  fontSize: 14,
  lineHeight: "18px",
  letterSpacing: 0,
  color: INTAKE_COLORS.textMuted,
};

function SystemMessageLogo() {
  return (
    <Box
      component="img"
      src={LogoIcon}
      alt=""
      sx={{
        width: SYSTEM_MSG_LOGO_WIDTH,
        height: SYSTEM_MSG_LOGO_HEIGHT,
        display: "block",
        objectFit: "contain",
        flexShrink: 0,
      }}
    />
  );
}

function visibleMessageLabel(label) {
  const text = String(label || "").trim();
  if (!text || /^Turn \d+$/i.test(text)) return null;
  return text;
}

function SystemMessageHeader({ label }) {
  const visibleLabel = visibleMessageLabel(label);
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mb: 0.5 }}>
      <SystemMessageLogo />
      <Typography component="span" sx={SYSTEM_MSG_NAME_SX}>
        ValidAite
      </Typography>
      {visibleLabel ? (
        <Typography component="span" sx={SYSTEM_MSG_TURN_SX}>
          {visibleLabel}
        </Typography>
      ) : null}
    </Box>
  );
}

function TypingIndicator() {
  return (
    <Box sx={{ display: "inline-flex", gap: 0.5, p: "11px 14px", bgcolor: INTAKE_COLORS.g1, borderRadius: "4px 14px 14px 14px" }}>
      {[0, 1, 2].map((i) => (
        <Box key={i} sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: INTAKE_COLORS.g3, animation: "intakeBounce 1.2s infinite", animationDelay: `${i * 0.2}s` }} />
      ))}
    </Box>
  );
}

function ScorePill({ pill }) {
  const [expanded, setExpanded] = useState(false);
  const changes = pill.changes || [];
  const previewCount = pill.previewCount ?? 4;
  const hasMore = changes.length > previewCount;
  const visible = expanded || !hasMore ? changes : changes.slice(0, previewCount);
  const hiddenCount = hasMore && !expanded ? changes.length - previewCount : 0;

  const shellSx = {
    my: 1.5,
    mx: "auto",
    px: 1.75,
    py: 0.625,
    borderRadius: "20px",
    bgcolor: pill.subtle ? INTAKE_COLORS.surface : INTAKE_COLORS.primary20,
    border: `1px solid ${pill.subtle ? INTAKE_COLORS.border : INTAKE_COLORS.primary40}`,
    color: pill.subtle ? INTAKE_COLORS.textMuted : INTAKE_COLORS.primary,
    fontSize: 11,
    fontWeight: pill.subtle ? 500 : 700,
    display: "flex",
    alignItems: "flex-start",
    gap: 0.625,
    width: "fit-content",
    maxWidth: "100%",
    flexWrap: "wrap",
  };

  const dot = (
    <Box
      sx={{
        width: 6,
        height: 6,
        borderRadius: "50%",
        bgcolor: pill.subtle ? INTAKE_COLORS.g3 : INTAKE_COLORS.primary,
        mt: 0.5,
        flexShrink: 0,
      }}
    />
  );

  if (!changes.length) {
    return (
      <Box sx={shellSx}>
        {dot}
        <Box dangerouslySetInnerHTML={{ __html: pill.text }} />
      </Box>
    );
  }

  return (
    <Box sx={shellSx}>
      {dot}
      <Box sx={{ lineHeight: 1.55 }}>
        {pill.prefix && (
          <Box component="span" sx={{ mr: 0.5 }}>
            {pill.prefix}
            {" · "}
          </Box>
        )}
        {visible.map((change, i) => (
          <Box key={change.id || i} component="span">
            {i > 0 && <Box component="span"> · </Box>}
            <Box
              component="span"
              dangerouslySetInnerHTML={{ __html: formatScoreChangePill(change) }}
            />
          </Box>
        ))}
        {hiddenCount > 0 && (
          <Box
            component="button"
            type="button"
            onClick={() => setExpanded(true)}
            sx={{
              border: "none",
              background: "none",
              p: 0,
              ml: 0.25,
              cursor: "pointer",
              font: "inherit",
              fontSize: "inherit",
              fontWeight: 700,
              color: INTAKE_COLORS.primary,
              textDecoration: "underline",
            }}
          >
            {" · "}+{hiddenCount} more
          </Box>
        )}
        {expanded && hasMore && (
          <Box
            component="button"
            type="button"
            onClick={() => setExpanded(false)}
            sx={{
              border: "none",
              background: "none",
              p: 0,
              ml: 0.25,
              cursor: "pointer",
              font: "inherit",
              fontSize: "inherit",
              fontWeight: 600,
              color: INTAKE_COLORS.textMuted,
              textDecoration: "underline",
            }}
          >
            {" · "}show less
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default function IntakeChatCenter({
  messages,
  scorePills,
  nextTarget,
  captured,
  sections,
  loading,
  uploadInProgress = false,
  gate0PrefillCount,
  overallPct = 0,
  isComplete = false,
  projectName = "",
  onSendChat,
  onPaste,
  onUpload,
  onEditMessage,
  onCancelUpload,
  onViewSummary,
}) {
  const { user } = useAuth();
  const userInitials = getUserInitials(user);
  const [mode, setMode] = useState("chat");
  const [input, setInput] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [pasteChars, setPasteChars] = useState(0);
  const [extracting, setExtracting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [inputError, setInputError] = useState("");
  const [essentialsDismissed, setEssentialsDismissed] = useState(false);
  const fileRef = useRef(null);
  const inputRef = useRef(null);
  const inputErrorTimerRef = useRef(null);

  const clearInputError = () => {
    if (inputErrorTimerRef.current) {
      clearTimeout(inputErrorTimerRef.current);
      inputErrorTimerRef.current = null;
    }
    setInputError("");
  };

  const showComposerError = (msg) => {
    setInputError(msg);
    if (inputErrorTimerRef.current) clearTimeout(inputErrorTimerRef.current);
    inputErrorTimerRef.current = setTimeout(() => {
      setInputError("");
      inputErrorTimerRef.current = null;
    }, 15000);
  };

  useEffect(() => () => {
    if (inputErrorTimerRef.current) clearTimeout(inputErrorTimerRef.current);
  }, []);

  useEffect(() => {
    if (!inputError) return undefined;
    const hide = () => {
      if (inputErrorTimerRef.current) {
        clearTimeout(inputErrorTimerRef.current);
        inputErrorTimerRef.current = null;
      }
      setInputError("");
    };
    const arm = window.setTimeout(() => {
      window.addEventListener("pointerdown", hide);
      window.addEventListener("keydown", hide);
    }, 400);
    return () => {
      window.clearTimeout(arm);
      window.removeEventListener("pointerdown", hide);
      window.removeEventListener("keydown", hide);
    };
  }, [inputError]);

  const resetFileInput = () => {
    if (fileRef.current) fileRef.current.value = "";
  };

  const openFilePicker = () => {
    if (uploadInProgress || extracting || loading) return;
    resetFileInput();
    fileRef.current?.click();
  };
  const msgsRef = useRef(null);
  const editRef = useRef(null);
  const [showScrollDown, setShowScrollDown] = useState(false);

  const placedPills = useMemo(
    () => placeScorePills(messages, scorePills),
    [messages, scorePills]
  );

  useEffect(() => {
    if (msgsRef.current) {
      msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
    }
  }, [messages, scorePills, loading, extracting, editingId]);

  useEffect(() => {
    if (editingId && editRef.current) {
      editRef.current.focus();
      editRef.current.style.height = "auto";
      editRef.current.style.height = `${Math.min(editRef.current.scrollHeight, 160)}px`;
    }
  }, [editingId, editDraft]);

  useEffect(() => {
    if (!uploadInProgress && !loading && extracting) {
      setExtracting(false);
    }
  }, [uploadInProgress, loading, extracting]);

  const scrollBottom = (smooth = false) => {
    const el = msgsRef.current;
    if (!el) return;
    if (smooth) {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
      return;
    }
    setTimeout(() => {
      if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight;
    }, 50);
  };

  useEffect(() => {
    const el = msgsRef.current;
    if (!el) return undefined;
    const onScroll = () => {
      const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
      setShowScrollDown(distance > 48);
    };
    onScroll();
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [messages, loading, extracting]);

  const handleSend = async () => {
    const text = input.trim();
    if (loading && !uploadInProgress) return;
    if (uploadInProgress) return;

    if (pendingFile) {
      if (isIntakeUploadTooLarge(pendingFile)) {
        showComposerError(INTAKE_UPLOAD_TOO_LARGE_MSG);
        setPendingFile(null);
        resetFileInput();
        return;
      }
      if (!isIntakeUploadTypeAllowed(pendingFile)) {
        showComposerError(INTAKE_UPLOAD_UNSUPPORTED_MSG);
        setPendingFile(null);
        resetFileInput();
        return;
      }
      setExtracting(true);
      const result = await onUpload(pendingFile);
      setExtracting(false);
      if (result?.cancelled) {
        setPendingFile(null);
        setInput("");
        setMode("chat");
        if (fileRef.current) fileRef.current.value = "";
        return;
      }
      if (!result?.error) {
        setPendingFile(null);
      }
      resetFileInput();
      setInput("");
      setMode("chat");
      scrollBottom();
      return;
    }

    if (looksLikeDocument(text)) {
      const docText = text;
      setInput("");
      setPasteChars(0);
      setExtracting(true);
      await onPaste(docText);
      setExtracting(false);
      setMode("chat");
      scrollBottom();
      return;
    }

    if (!text) return;
    const validated = validateIntakeText(text);
    if (!validated.ok) {
      showComposerError(validated.message);
      return;
    }
    clearInputError();
    setInput("");
    const sent = await onSendChat(validated.value);
    if (sent === false) {
      setInput(text);
    }
    scrollBottom();
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCancelExtraction = () => {
    if (onCancelUpload) onCancelUpload();
    setExtracting(false);
    setPendingFile(null);
    setMode("chat");
    resetFileInput();
  };

  const handleFile = (file) => {
    if (!file) return;
    if (loading || extracting || uploadInProgress) return;
    if (isIntakeUploadTooLarge(file)) {
      showComposerError(INTAKE_UPLOAD_TOO_LARGE_MSG);
      setPendingFile(null);
      resetFileInput();
      return;
    }
    if (!isIntakeUploadTypeAllowed(file)) {
      showComposerError(INTAKE_UPLOAD_UNSUPPORTED_MSG);
      setPendingFile(null);
      resetFileInput();
      return;
    }
    setInputError("");
    setPendingFile(file);
    setMode("chat");
  };

  const startEdit = (msg) => {
    if (loading || !onEditMessage || isSystemUserMessage(msg)) return;
    setEditingId(msg.id);
    setEditDraft(msg.content || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft("");
  };

  const saveEdit = async () => {
    const text = editDraft.trim();
    if (!text || !editingId || loading) return;
    const messageId = editingId;
    setEditingId(null);
    setEditDraft("");
    await onEditMessage(messageId, text);
    scrollBottom();
  };

  const handleEditKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      saveEdit();
    }
    if (e.key === "Escape") {
      e.preventDefault();
      cancelEdit();
    }
  };

  useEffect(() => {
    if (!isComplete) setEssentialsDismissed(false);
  }, [isComplete]);

  const showSmartBanner = messages.length <= 2 && !isComplete;
  const showEssentialsCta = Boolean(onViewSummary) && isComplete && !essentialsDismissed;

  return (
    <Box sx={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, bgcolor: INTAKE_COLORS.bg, fontFamily: INTAKE_FONT }}>
      <style>{`@keyframes intakeBounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-5px)} }`}</style>

      <Box
        ref={msgsRef}
        sx={{
          flex: 1,
          overflow: "auto",
          pt: 2.5,
          px: 2.5,
          pb: "16px",
          width: "100%",
          minWidth: 0,
          bgcolor: INTAKE_COLORS.white,
        }}
      >
          {showSmartBanner && (
            <Box
              sx={{
                mb: 2,
                p: 1.75,
                borderRadius: "10px",
                bgcolor: INTAKE_COLORS.purpleBanner,
                border: `1px solid ${INTAKE_COLORS.purpleLight}`,
                display: "flex",
                gap: 1.25,
                alignItems: "flex-start",
              }}
            >
              <AutoAwesomeIcon sx={{ fontSize: 20, color: INTAKE_COLORS.purple, mt: 0.25 }} />
              <Box>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: INTAKE_COLORS.text, mb: 0.5 }}>
                  Smart intake is active
                </Typography>
                <Typography sx={{ fontSize: 12, color: INTAKE_COLORS.textSecondary, lineHeight: 1.55 }}>
                  Smart intake is active. Answer in chat or upload documents — captured facts appear in the left panel.
                </Typography>
              </Box>
            </Box>
          )}

          {gate0PrefillCount > 0 && (
            <Box
              sx={{
                mb: 1.75,
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                width: "100%",
                minHeight: 48,
                boxSizing: "border-box",
                px: "16px",
                py: "8px",
                gap: "16px",
                borderRadius: "8px",
                bgcolor: "#F0F8FF",
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  flexShrink: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <BlueFilledAlertIcon style={{ width: 32, height: 32, display: "block" }} />
              </Box>
              <Typography
                sx={{
                  flex: 1,
                  minWidth: 0,
                  fontFamily: "Lato, sans-serif",
                  fontWeight: 700,
                  fontSize: 14,
                  lineHeight: "21px",
                  letterSpacing: 0,
                  color: "#0046B8",
                }}
              >
                {gate0PrefillCount} item{gate0PrefillCount === 1 ? " has" : "s have"} been pre-filled based on your onboarding answers.
              </Typography>
            </Box>
          )}

          {messages.map((msg) => {
            const userTyped = isUserTypedMessage(msg);
            const anchoredPills = placedPills.byMessageId.get(msg.id) || [];
            const geoValidation =
              msg.role === "assistant" && !msg.outputs && isGeoValidationReply(msg.content);
            const isUploadMessage = 
            msg.role === "user" && String(msg.id || "").startsWith("upload-");
            const uploadError =
              msg.role === "assistant" && !msg.outputs && (msg.isError || /\*\*upload failed\*\*/i.test(msg.content || ""));
            const alertStyle = geoValidation || uploadError;

            const userLabel = visibleMessageLabel(msg.label);

            return (
            <Box key={msg.id} sx={{ width: "100%" }}>
            <Box
              sx={{
                mb: 0.5,
                display: "flex",
                justifyContent: userTyped ? "flex-end" : "flex-start",
                width: "100%",
              }}
            >
              {isAssistantMessage(msg) ? (
                <Box sx={{ maxWidth: "80%", width: "fit-content", minWidth: 0 }}>
                  <SystemMessageHeader label={msg.label} />
                  {msg.validation && msg.validation.valid === false && (
                    <Box
                      sx={{
                        mb: 1,
                        p: "10px 12px",
                        bgcolor: "#FFF8E6",
                        border: "1px solid #F5C542",
                        borderRadius: "8px",
                        fontSize: 12,
                        color: "#7A5B00",
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>
                        Validation warning
                      </Typography>
                      <Typography sx={{ fontSize: 12 }}>
                        {msg.validation.primaryMessage || "Please review and correct the inconsistent information."}
                      </Typography>
                    </Box>
                  )}
                  <ChatMessageBubble message={msg}>
                    {msg.outputs ? <OutputCard outputs={msg.outputs} /> : (
                      <Box dangerouslySetInnerHTML={{ __html: formatAgentReply(msg.content) }} />
                    )}
                  </ChatMessageBubble>
                  {msg.scorePill && <ScorePill pill={msg.scorePill} />}
                </Box>
              ) : (
                <Box
                  sx={{
                    maxWidth: "80%",
                    width: "fit-content",
                    minWidth: 0,
                    ml: userTyped ? "auto" : 0,
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, justifyContent: userTyped ? "flex-end" : "flex-start" }}>
                    {userLabel && <Typography sx={{ fontSize: 10, color: INTAKE_COLORS.textMuted }}>{userLabel}</Typography>}
                    {editingId !== msg.id && onEditMessage && !isSystemUserMessage(msg) && !loading && (
                      <Box
                        component="button"
                        onClick={() => startEdit(msg)}
                        sx={{
                          border: "none",
                          bgcolor: "transparent",
                          color: INTAKE_COLORS.g4,
                          fontSize: 10,
                          fontWeight: 600,
                          cursor: "pointer",
                          px: 0.5,
                          fontFamily: INTAKE_FONT,
                          "&:hover": { color: INTAKE_COLORS.blue },
                        }}
                      >
                        Edit
                      </Box>
                    )}
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: INTAKE_COLORS.textMuted }}>You</Typography>
                    <Box
                      component="span"
                      aria-label={`You (${userInitials})`}
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        bgcolor: "#555555",
                        color: "#fff",
                        fontFamily: "Lato, sans-serif",
                        fontSize: 10,
                        fontWeight: 600,
                        lineHeight: "10px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                        letterSpacing: "0.02em",
                        textTransform: "uppercase",
                      }}
                    >
                      {userInitials}
                    </Box>
                  </Box>
                  {editingId === msg.id ? (
                    <Box sx={{ p: 1.25, bgcolor: INTAKE_COLORS.white, border: `1.5px solid ${INTAKE_COLORS.blue}`, borderRadius: "10px" }}>
                      <Box
                        component="textarea"
                        ref={editRef}
                        value={editDraft}
                        onChange={(e) => {
                          setEditDraft(e.target.value);
                          e.target.style.height = "auto";
                          e.target.style.height = `${Math.min(e.target.scrollHeight, 160)}px`;
                        }}
                        onKeyDown={handleEditKey}
                        sx={{
                          width: "100%",
                          border: "none",
                          outline: "none",
                          fontFamily: INTAKE_FONT,
                          fontSize: 13,
                          lineHeight: 1.65,
                          resize: "none",
                          minHeight: 56,
                          maxHeight: 160,
                          bgcolor: "transparent",
                        }}
                      />
                      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.75, mt: 1 }}>
                        <Box
                          component="button"
                          onClick={cancelEdit}
                          sx={{
                            px: 1.25,
                            py: 0.5,
                            border: `1px solid ${INTAKE_COLORS.g2}`,
                            borderRadius: "7px",
                            bgcolor: INTAKE_COLORS.white,
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: "pointer",
                            fontFamily: INTAKE_FONT,
                          }}
                        >
                          Cancel
                        </Box>
                        <Box
                          component="button"
                          onClick={saveEdit}
                          disabled={!editDraft.trim()}
                          sx={{
                            px: 1.25,
                            py: 0.5,
                            border: "none",
                            borderRadius: "7px",
                            bgcolor: INTAKE_COLORS.blue,
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: editDraft.trim() ? "pointer" : "default",
                            opacity: editDraft.trim() ? 1 : 0.5,
                            fontFamily: INTAKE_FONT,
                          }}
                        >
                          Save & resend
                        </Box>
                      </Box>
                      <Typography sx={{ fontSize: 10, color: INTAKE_COLORS.g4, mt: 0.75 }}>
                        This removes later messages and recalculates scores from this point.
                      </Typography>
                    </Box>
                  ) : (
                    <ChatMessageBubble message={msg}>
                      {isUploadMessage ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <DocIcon style={{ width: 14, height: 16, flexShrink: 0 }} />
                          <Box component="span">
                            {msg.content.replace(/^📄\uFE0F?\s*/, "")}
                          </Box>
                        </Box>
                      ) : (
                        <Box component="span">{msg.content}</Box>
                      )}
                    </ChatMessageBubble>
                  )}
                </Box>
              )}
            </Box>
            {anchoredPills.map((pill) => (
              <ScorePill key={pill.id} pill={pill} />
            ))}
            </Box>
            );
          })}

          {placedPills.trailing.map((pill) => (
            <ScorePill key={pill.id} pill={pill} />
          ))}

          {showEssentialsCta && onViewSummary && (
            <EssentialsCompleteCard
              overallPct={overallPct}
              projectName={projectName}
              onViewSummary={onViewSummary}
              onStayInChat={() => {
                setEssentialsDismissed(true);
                inputRef.current?.focus();
              }}
            />
          )}

          {loading && !extracting && !uploadInProgress && (
            <Box sx={{ mb: 1.75, maxWidth: "100%", width: "fit-content" }}>
              <SystemMessageHeader />
              <TypingIndicator />
            </Box>
          )}

          {(extracting || uploadInProgress) && (
            <ExtractionProgressCard
              sections={sections}
              totalSections={sections?.length}
              active={extracting || uploadInProgress}
              onCancel={onCancelUpload ? handleCancelExtraction : undefined}
            />
          )}
      </Box>

      <Box
        sx={{
          position: "relative",
          px: 2.5,
          py: 1.5,
          flexShrink: 0,
          bgcolor: "#FFFFFF",
          width: "100%",
          boxSizing: "border-box",
        }}
        onClick={clearInputError}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          if (!uploadInProgress && !extracting && e.dataTransfer.files?.[0]) {
            handleFile(e.dataTransfer.files[0]);
          }
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: 0,
            right: 0,
            bottom: "100%",
            height: 8,
            pointerEvents: "none",
            background: "linear-gradient(to bottom, rgba(255,255,255,0) 0%, #FFFFFF 85%)",
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              backdropFilter: "blur(4x)",
              WebkitBackdropFilter: "blur(4x)",
              maskImage: "linear-gradient(to bottom, transparent 0%, #000 100%)",
              WebkitMaskImage: "linear-gradient(to bottom, transparent 0%, #000 100%)",
            },
          }}
        />
        {showScrollDown && (
          <Box
            component="button"
            type="button"
            aria-label="Scroll to latest message"
            onClick={() => scrollBottom(true)}
            sx={{
              position: "absolute",
              left: "50%",
              top: 0,
              transform: "translate(-50%, calc(-100% - 8px))",
              zIndex: 2,
              width: 32,
              height: 32,
              p: 0,
              borderRadius: "50%",
              border: "1px solid #E6E6E6",
              bgcolor: "#FFFFFF",
              color: "#383838",
              boxShadow: "0px 2px 8px 0px #00000014",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              "&:hover": { bgcolor: "#F5F5F5" },
            }}
          >
            <KeyboardArrowDownIcon sx={{ fontSize: 20 }} />
          </Box>
        )}
        <Box sx={{ width: "100%", maxWidth: "100%" }}>
          {pendingFile && (
            <Box sx={{ mb: 0.875, p: "6px 10px", bgcolor: INTAKE_COLORS.blueLight, borderRadius: "7px", fontSize: 11, color: INTAKE_COLORS.blue, fontWeight: 600, display: "flex", alignItems: "center", gap: 0.875 }}>
              <span>📄</span>
              <span>{pendingFile.name}</span>
              <span style={{ color: INTAKE_COLORS.g4, fontWeight: 400 }}>{(pendingFile.size / 1024).toFixed(0)} KB</span>
              <Box
                component="span"
                onClick={() => {
                  if (extracting || uploadInProgress) {
                    handleCancelExtraction();
                    return;
                  }
                  setPendingFile(null);
                  if (fileRef.current) fileRef.current.value = "";
                }}
                sx={{ ml: "auto", cursor: "pointer", color: INTAKE_COLORS.g4 }}
                title={extracting || uploadInProgress ? "Stop extraction" : "Remove file"}
              >
                ✕
              </Box>
            </Box>
          )}

          {looksLikeDocument(input) && (
            <Box sx={{ mb: 0.875, p: "6px 10px", bgcolor: "#f0fdf4", border: "1px solid rgba(22,163,74,.2)", borderRadius: "7px", fontSize: 11, color: INTAKE_COLORS.greenDark, fontWeight: 600 }}>
              📋 {pasteChars.toLocaleString()} chars — will extract across all sections
            </Box>
          )}

          {inputError && (
            <Typography
              sx={{
                fontFamily: "'Lato', sans-serif",
                fontSize: 16,
                fontWeight: 700,
                lineHeight: "24px",
                color: "#DE350B",
                mb: 0.75,
              }}
            >
              {inputError}
            </Typography>
          )}

          <Box
            sx={{
              border: `1px solid ${inputError ? INTAKE_COLORS.red : INTAKE_COLORS.textMuted}`,
              borderRadius: "12px",
              bgcolor: INTAKE_COLORS.surface,
              boxShadow: "0px 2px 3px 1px #55555533 inset",
              overflow: "hidden",
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.25,
              py: 0.75,
              minHeight: 48,
              boxSizing: "border-box",
              transition: "border-color 0.15s ease, border-width 0.15s ease",
              "&:focus-within": {
                border: `1px solid ${inputError ? INTAKE_COLORS.red : "#3895FF"}`,
              },
            }}
          >
            <Box
              component="button"
              type="button"
              onClick={openFilePicker}
              sx={{
                width: 32,
                height: 32,
                flexShrink: 0,
                border: "none",
                p: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: loading || extracting || uploadInProgress ? "default" : "pointer",
                bgcolor: "transparent",
                opacity: loading || extracting || uploadInProgress ? 0.4 : 1,
              }}
              title="Attach file"
            >
              <UploadButtonIcon style={{ width: 18, height: 18, display: "block" }} />
            </Box>
            <Box
              component="textarea"
              ref={inputRef}
              rows={1}
              value={input}
              disabled={loading || uploadInProgress}
              onChange={(e) => {
                setInput(e.target.value);
                if (inputError) clearInputError();
                setPasteChars(e.target.value.length);
                if (!pendingFile) {
                  setMode(looksLikeDocument(e.target.value) ? "paste" : "chat");
                }
                e.target.style.height = "auto";
                e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
              }}
              onPaste={(e) => {
                const clip = e.clipboardData?.getData("text") || "";
                if (looksLikeDocument(clip, true)) {
                  setMode("paste");
                  setPasteChars(clip.length);
                }
              }}
              onKeyDown={handleKey}
              placeholder={
                pendingFile
                  ? "Add a note or click Send to extract..."
                  : "Reply, paste a link, or drop a document..."
              }
              sx={{
                display: "block",
                flex: "1 1 auto",
                minWidth: 0,
                width: "100%",
                border: "none",
                outline: "none",
                px: 0,
                py: 0.5,
                m: 0,
                fontFamily: INTAKE_FONT,
                fontSize: 13,
                resize: "none",
                minHeight: 24,
                lineHeight: 1.45,
                maxHeight: 120,
                bgcolor: "transparent",
                boxSizing: "border-box",
                color: INTAKE_COLORS.text,
                alignSelf: "center",
                "&::placeholder": { color: INTAKE_COLORS.g4 },
              }}
            />
            <Box
              component="button"
              type="button"
              onClick={handleSend}
              disabled={loading || uploadInProgress || (!input.trim() && !pendingFile)}
              sx={{
                width: 36,
                height: 36,
                flexShrink: 0,
                border: "none",
                borderRadius: "8px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 0,
                boxShadow: SEND_BTN_SHADOW,
                cursor: loading || uploadInProgress || (!input.trim() && !pendingFile) ? "default" : "pointer",
                bgcolor: INTAKE_COLORS.g2,
                backgroundImage: "none",
                "&:not(:disabled)": {
                  bgcolor: "transparent",
                  backgroundImage: SEND_BTN_BG_DEFAULT,
                  "&:hover": {
                    backgroundImage: SEND_BTN_BG_HOVER,
                    boxShadow: SEND_BTN_SHADOW,
                  },
                },
                "&:disabled": {
                  boxShadow: SEND_BTN_SHADOW,
                  bgcolor: INTAKE_COLORS.g2,
                  backgroundImage: "none",
                  opacity: 0.65,
                },
              }}
              title="Send"
            >
              {loading ? (
                <CircularProgress size={16} sx={{ color: "#fff" }} />
              ) : (
                <UpArrowIcon style={{ width: 18, height: 18, display: "block" }} />
              )}
            </Box>
          </Box>

          <input
            ref={fileRef}
            type="file"
            accept={INTAKE_UPLOAD_ACCEPT}
            hidden
            onChange={(e) => {
              const file = e.target.files?.[0];
              handleFile(file);
              resetFileInput();
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}